import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getTemplates, deleteTemplate, generateMissionFromTemplate, updateTemplate } from '../../services/missionTemplateService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PageHeader from '../../components/common/PageHeader';
import FilterCard from '../../components/common/FilterCard';
import { useI18n } from '../../i18n/I18nContext';
import './MissionTemplate.css';

const MissionTemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [generatingId, setGeneratingId] = useState(null);
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await getTemplates();
      setTemplates(data);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirmDelete') || 'Êtes-vous sûr de vouloir supprimer ce modèle ?')) return;
    try {
      await deleteTemplate(id);
      setTemplates(templates.filter(t => t.id !== id));
      toast.success(t('common.deletedSuccess') || 'Modèle supprimé avec succès');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleGenerateNow = async (id) => {
    try {
      setGeneratingId(id);
      const mission = await generateMissionFromTemplate(id);
      if (mission.duplicateWarning) {
        toast.warning('⚠️ Mission générée, mais une mission similaire existe déjà aujourd\'hui !', { autoClose: 6000 });
      } else {
        toast.success(t('missions.recurring.generatedSuccess') || 'Mission générée avec succès !');
      }
      // Refresh to update next execution date
      fetchTemplates();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGeneratingId(null);
    }
  };

  const toggleActive = async (template) => {
    try {
      const updated = await updateTemplate(template.id, { ...template, isActive: !template.isActive });
      setTemplates(templates.map(t => t.id === template.id ? updated : t));
      const message = updated.isActive 
        ? (t('missions.recurring.activatedSuccess') || 'Modèle activé') 
        : (t('missions.recurring.deactivatedSuccess') || 'Modèle désactivé');
      toast.success(message);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(search.toLowerCase()) ||
    template.departureLocation.toLowerCase().includes(search.toLowerCase()) ||
    template.arrivalLocation.toLowerCase().includes(search.toLowerCase())
  );

  if (loading && templates.length === 0) {
    return (
      <DashboardLayout>
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container-fluid py-4">
        <PageHeader
          title={t('missions.recurring.title') || 'Missions Récurrentes'}
          subtitle={t('missions.recurring.subtitle') || 'Gérez vos modèles de missions automatiques'}
          buttonText={t('missions.recurring.newTemplate') || 'Nouveau Modèle'}
          buttonTo="/missions/recurring/create"
        />

        <FilterCard>
          <div className="row g-3 align-items-center">
            <div className="col-md-12">
              <div className="search-box-premium">
                <i className="bi bi-search search-icon"></i>
                <input
                  type="text"
                  className="form-control premium-input"
                  placeholder={t('missions.recurring.searchPlaceholder') || 'Rechercher par nom, départ ou arrivée...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </FilterCard>

        {filteredTemplates.length === 0 ? (
          <div className="empty-state-card card shadow-sm border-0 text-center p-5 mt-4">
            <div className="empty-state-icon mb-3">
              <i className="bi bi-calendar-event text-primary opacity-50" style={{ fontSize: '4rem' }}></i>
            </div>
            <h3>{t('missions.recurring.noTemplates') || 'Aucun modèle trouvé'}</h3>
            <p className="text-muted">{t('missions.recurring.createFirst') || 'Commencez par créer votre premier modèle de mission récurrente.'}</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate('/missions/recurring/create')}>
              <i className="bi bi-plus-lg me-2"></i>{t('missions.recurring.newTemplate') || 'Nouveau Modèle'}
            </button>
          </div>
        ) : (
          <div className="row g-4 mt-2">
            {filteredTemplates.map((template) => (
              <div className="col-12 col-xl-6" key={template.id}>
                <div className={`premium-template-card ${template.isActive ? 'active' : 'inactive'}`}>
                  <div className="card-header-premium">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h4 className="template-title mb-1">{template.name}</h4>
                        <div className="template-days">
                          {template.templateMode === 'SIMPLE' ? (
                            <span className="badge bg-soft-primary text-primary border-primary-soft me-2" title="Génération immédiate">
                              <i className="bi bi-lightning-charge-fill me-1"></i> Mode Simple
                            </span>
                          ) : (
                            <span className="badge bg-soft-warning text-warning border-warning-soft me-2" title="Génération avec anticipation">
                              <i className="bi bi-gear-wide-connected me-1"></i> Mode Avancé
                            </span>
                          )}
                          {template.frequencyType === 'MANUAL' ? (
                            <span className="badge bg-soft-secondary text-secondary border-secondary-soft me-2">
                              <i className="bi bi-hand-index-thumb me-1"></i> Manuel
                            </span>
                          ) : (
                            <span className="badge bg-soft-success text-success border-success-soft me-2">
                              <i className="bi bi-robot me-1"></i> 
                              {template.frequencyType === 'DAILY' && `Chaque ${template.intervalValue > 1 ? template.intervalValue + ' jours' : 'jour'}`}
                              {template.frequencyType === 'WEEKLY' && `Chaque ${template.intervalValue > 1 ? template.intervalValue + ' semaines' : 'semaine'} (${template.daysOfWeek ? template.daysOfWeek.split(',').map(d => d.substring(0,3)).join(', ') : ''})`}
                              {template.frequencyType === 'MONTHLY_DATE' && `Chaque ${template.intervalValue > 1 ? template.intervalValue + ' mois' : 'mois'} (Le ${template.monthDay})`}
                              {template.frequencyType === 'MONTHLY_DAY' && `Chaque ${template.intervalValue > 1 ? template.intervalValue + ' mois' : 'mois'} (Pos. ${template.weekOfMonth})`}
                              {template.frequencyType === 'CUSTOM' && `Tous les ${template.intervalValue} jours`}
                            </span>
                          )}
                          {template.isInternalMission ? (
                            <span className="badge bg-soft-secondary text-secondary border-secondary-soft">
                              <i className="bi bi-building me-1"></i> Mission Interne
                            </span>
                          ) : (
                            template.clientNom && (
                              <span className="badge bg-soft-info text-info border-info-soft">
                                <i className="bi bi-person-fill me-1"></i> {template.clientPrenom} {template.clientNom}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className={`fw-bold small ${template.isActive ? 'text-success' : 'text-muted'}`}>
                          {template.isActive ? 'Actif' : 'Inactif'}
                        </span>
                        <div className="form-check form-switch p-0 m-0">
                          <input
                            className="form-check-input premium-switch"
                            type="checkbox"
                            checked={template.isActive}
                            onChange={() => toggleActive(template)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body-premium">
                    <div className="route-flow mb-4">
                      <div className="route-point">
                        <div className="point-icon departure">
                          <i className="bi bi-geo-alt-fill"></i>
                        </div>
                        <div className="point-details">
                          <label>{t('common.departure') || 'Départ'}</label>
                          <div className="location-name">{template.departureLocation}</div>
                          <div className="point-time fw-bold text-primary">
                            {template.timeSlots && template.timeSlots.length > 0 ? 
                              template.timeSlots.map(t => t.departureTime.substring(0, 5)).join(' | ') : 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="route-line"></div>
                      <div className="route-point">
                        <div className="point-icon arrival">
                          <i className="bi bi-flag-fill"></i>
                        </div>
                        <div className="point-details">
                          <label>{t('common.arrival') || 'Arrivée'}</label>
                          <div className="location-name">{template.arrivalLocation}</div>
                          <div className="point-time fw-bold text-primary">
                            {template.timeSlots && template.timeSlots.length > 0 ? 
                              template.timeSlots.map(t => t.arrivalTime.substring(0, 5)).join(' | ') : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="template-metadata">
                      <div className="meta-item">
                        <i className="bi bi-box-seam me-2"></i>
                        <span>{template.cargoType}</span>
                      </div>
                      <div className="meta-item">
                        <i className="bi bi-truck me-2"></i>
                        <span>{template.vehicleType}</span>
                      </div>
                      <div className="meta-item text-primary fw-bold">
                        <i className="bi bi-currency-euro me-2"></i>
                        <span>{template.price} {template.currency || '€'}</span>
                      </div>
                    </div>
                    
                    {/* Default Resources Section */}
                    {(template.defaultChauffeurId || template.defaultVehiculeId || template.defaultRemorqueId) && (
                      <div className="mt-3 p-2 bg-light rounded-3 d-flex gap-3 small border border-light">
                        {template.defaultChauffeurId && (
                          <div className="d-flex align-items-center text-muted">
                            <i className="bi bi-person-badge me-1"></i> Chauffeur assigné
                          </div>
                        )}
                        {template.defaultVehiculeId && (
                          <div className="d-flex align-items-center text-muted">
                            <i className="bi bi-truck me-1"></i> Véhicule assigné
                          </div>
                        )}
                        {template.defaultRemorqueId && (
                          <div className="d-flex align-items-center text-muted">
                            <i className="bi bi-trailer me-1"></i> Remorque assignée
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="card-footer-premium">
                    <div className="next-execution">
                      <i className="bi bi-clock-history me-2"></i>
                      {template.frequencyType !== 'MANUAL' ? (
                        <>
                          <span>{t('missions.recurring.nextExecution') || 'Prochaine exécution :'} </span>
                          <strong>{template.nextExecutionDate ? new Date(template.nextExecutionDate).toLocaleString() : 'N/A'}</strong>
                        </>
                      ) : (
                        <span className="text-muted italic">Modèle à la demande - Cliquez sur Play pour créer</span>
                      )}
                    </div>
                    <div className="action-group">
                      <button 
                        className="btn btn-premium-action generate" 
                        title="Générer maintenant"
                        disabled={generatingId === template.id}
                        onClick={() => handleGenerateNow(template.id)}
                      >
                        {generatingId === template.id ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-play-fill text-success"></i>
                        )}
                      </button>
                      <button 
                        className="btn btn-premium-action edit" 
                        title="Modifier"
                        onClick={() => navigate(`/missions/recurring/edit/${template.id}`)}
                      >
                        <i className="bi bi-pencil-fill text-primary"></i>
                      </button>
                      <button 
                        className="btn btn-premium-action delete" 
                        title="Supprimer"
                        onClick={() => handleDelete(template.id)}
                      >
                        <i className="bi bi-trash-fill text-danger"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MissionTemplateList;
